export type SignupSearch = {
  signupType?: string;
  promoCode?: string;
  /** Marketing-site prefills, copied into the funnel's form state on the entry step. */
  email?: string;
  first?: string;
  last?: string;
  /** Set with `email` by the join-request hand-off; resumes at the password step. */
  resume?: boolean;
} & Record<string, unknown>;

/**
 * TanStack Router's default `parseSearch` runs every value through `JSON.parse`,
 * so a numeric-looking `?promoCode=123` arrives as the number `123` — coerce it
 * back rather than dropping it (a dropped key is stripped from the URL entirely).
 */
function asString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  return typeof value === 'number' ? String(value) : undefined;
}

/**
 * `?resume=true` arrives JSON-parsed as the boolean `true`, so a string-only
 * coercion would drop it. Normalize both spellings to `true` here and nothing
 * else: downstream compares against `true` exactly, so no other truthy value
 * can open the verification-skipping path.
 */
function asResumeFlag(value: unknown): boolean | undefined {
  return value === true || value === 'true' ? true : undefined;
}

/**
 * Validate the `/signup` search params.
 *
 * Unknown keys pass through untouched — TanStack strips any key
 * `validateSearch` doesn't return, and this route also matches while the
 * embedded app serves the funnel (rollout flag off), so dropping `utm_*`/
 * `gclid`/`signupFlow`/`content` here would corrupt marketing attribution and
 * flow selection before the embedded app even boots. The signup flow's own
 * landing-param capture consumes them too.
 */
export function parseSignupSearch(search: Record<string, unknown>): SignupSearch {
  return {
    ...search,
    signupType: typeof search.signupType === 'string' ? search.signupType : undefined,
    promoCode: asString(search.promoCode),
    email: asString(search.email),
    first: asString(search.first),
    last: asString(search.last),
    resume: asResumeFlag(search.resume),
  };
}

/**
 * The verified email to resume signup with, or `null` when these params aren't
 * a resume hand-off from the join-request screen. That screen already collected
 * the 6-digit code, so the funnel skips the entry form and the code step.
 * Trimmed to the funnel guard's `email.trim() !== ''`, or whitespace would loop.
 */
export function resumeEmailFromParams(search: SignupSearch): string | null {
  if (search.resume !== true) {
    return null;
  }
  const email = search.email?.trim() ?? '';
  return email !== '' ? email : null;
}
